// AddProject — the operator-facing Add-Project wizard (PROP-67), the
// single guided flow that wraps the whole PROP-42 staged-ingest
// pipeline so a non-engineer never touches SQL or SSH-runs a script.
//
// Seven steps: Source → Extract → Rewrite → Enrich → Review →
// Leak-scan → Commit. Each backend call lives in
// @/lib/projectIngestApi; this component is the orchestration + the
// per-step UI. Session id lives in the URL (?session=) so a refresh
// resumes on the right step (server state drives stepFromState).
//
// Honesty rule (CLAUDE.md "Engineering owns data"): steps render only
// what the pipeline actually produced — the Extract feed shows real
// crawl events or an honest "no events yet" state, never a fabricated
// progress bar; Enrich explains what ran server-side rather than
// inventing geo values.

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  PageHeader, cardWrap, ghostBtn, primaryBtn, smallBtn,
} from "@/components/admin-ui";
import { useToast } from "@/components/ui-prims";
import { ApiError } from "@/lib/api";
import {
  dispatchProject, getSession, listSessions, advanceSession, abandonSession,
  runRewrite, getRewritePreview, applyRewrite,
  runLeakScan, acceptLeakHit, commitSession,
  stepFromState, stateForStep, stepIndex, WIZARD_STEPS, STEP_LABELS,
  type StagingSession, type StagingSource, type WizardStep,
  type RewritePayload, type LeakScanResult, type CommitResult,
} from "@/lib/projectIngestApi";

export function AddProjectPage() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session") ?? "";
  const toast = useToast();

  const [session, setSession] = useState<StagingSession | null>(null);
  const [step, setStep] = useState<WizardStep>("source");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load (or reload) the session behind ?session=. Derives the resume
  // step from the persisted state on first load only — once the
  // operator is moving through steps we don't yank them backward on a
  // routine poll/refetch.
  const loadedFor = useRef<string>("");
  const refetch = useCallback(async (): Promise<StagingSession | null> => {
    if (!sessionId) return null;
    const s = await getSession(sessionId);
    setSession(s);
    if (loadedFor.current !== sessionId) {
      loadedFor.current = sessionId;
      setStep(stepFromState(s.state));
    }
    return s;
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      loadedFor.current = "";
      setStep("source");
      return;
    }
    setLoading(true);
    refetch()
      .catch((e) => toast(e instanceof ApiError ? e.message : String(e), { tone: "error" }))
      .finally(() => setLoading(false));
  }, [sessionId, refetch, toast]);

  function selectSession(id: string) {
    setParams(id ? { session: id } : {});
  }

  // Advance server state to keep resume in sync, then move the UI step.
  // Idempotent server-side; we surface a non-fatal warning if it fails
  // but still move the UI so the operator isn't stuck.
  async function goToStep(next: WizardStep) {
    const target = stateForStep(next);
    if (sessionId && target) {
      try {
        const s = await advanceSession(sessionId, target);
        setSession(s);
      } catch (e) {
        // 409 = already past this state on resume; harmless, fall through.
        // Any OTHER error means the server did NOT advance — surface it and
        // STAY on the current step. Marching forward anyway used to dump the
        // operator on Commit with un-advanced server state, where dispatch
        // 409s into a dead end (PROP-208).
        if (!(e instanceof ApiError && e.status === 409)) {
          toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
          return;
        }
      }
    }
    setStep(next);
  }

  async function handleAbandon() {
    if (!sessionId || busy) return;
    if (!window.confirm("Abandon this session? Staged rows for the slug are discarded.")) return;
    setBusy(true);
    try {
      await abandonSession(sessionId);
      toast("Session abandoned — staged rows discarded.", { tone: "success" });
      selectSession("");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    } finally {
      setBusy(false);
    }
  }

  const slug = session?.slug ?? "";

  return (
    <>
      <PageHeader
        kicker="Data engine"
        title="Add project"
        sub={session
          ? `${slug} · source ${session.source} · state ${session.state}`
          : "Ingest a single project from a listing URL through to live, with a guided review."}
        actions={session && session.state !== "committed" && session.state !== "abandoned" ? (
          <button type="button" onClick={handleAbandon} disabled={busy} style={ghostBtn}>
            Abandon
          </button>
        ) : undefined}
      />

      <div style={{ padding: "0 26px 26px" }}>
        <StepRail current={step} />

        {loading ? (
          <div style={{ ...cardWrap, padding: 24, color: "var(--ink-3)" }}>Loading session…</div>
        ) : !session ? (
          <SourceStep
            busy={busy}
            onResume={selectSession}
            onDispatched={(id) => selectSession(id)}
            setBusy={setBusy}
          />
        ) : (
          <>
            {step === "extract" && (
              <ExtractStep session={session} onPoll={refetch} onNext={() => goToStep("rewrite")} />
            )}
            {step === "rewrite" && (
              <RewriteStep sessionId={session.id} onNext={() => goToStep("enrich")} />
            )}
            {step === "enrich" && (
              <EnrichStep onNext={() => goToStep("review")} />
            )}
            {step === "review" && (
              <ReviewStep slug={slug} onNext={() => goToStep("leak")} />
            )}
            {step === "leak" && (
              <LeakStep sessionId={session.id} slug={slug} onCommit={() => setStep("commit")} />
            )}
            {step === "commit" && (
              <CommitStep session={session} onCommitted={refetch} />
            )}
          </>
        )}
      </div>
    </>
  );
}

// ─── progress rail ──────────────────────────────────────────────────

function StepRail({ current }: { current: WizardStep }) {
  const curIdx = stepIndex(current);
  return (
    <div style={{ display: "flex", gap: 6, margin: "4px 0 18px", flexWrap: "wrap" }}>
      {WIZARD_STEPS.map((s, i) => {
        const done = i < curIdx;
        const active = i === curIdx;
        return (
          <div key={s} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "5px 11px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500,
            background: active ? "var(--accent)" : done ? "var(--accent-soft, #e6f0ea)" : "var(--surface-2, #f1f1f0)",
            color: active ? "#fff" : done ? "var(--accent)" : "var(--ink-3)",
          }}>
            <span style={{ opacity: 0.7 }}>{done ? "✓" : i + 1}</span>
            {STEP_LABELS[s]}
          </div>
        );
      })}
    </div>
  );
}

// ─── step 1: source ─────────────────────────────────────────────────

function SourceStep({
  busy, setBusy, onDispatched, onResume,
}: {
  busy: boolean;
  setBusy: (b: boolean) => void;
  onDispatched: (sessionId: string) => void;
  onResume: (sessionId: string) => void;
}) {
  const toast = useToast();
  const [source, setSource] = useState<StagingSource>("cos");
  const [url, setUrl] = useState("");
  const [manualName, setManualName] = useState("");
  const [inflight, setInflight] = useState<StagingSession[]>([]);

  useEffect(() => {
    listSessions().then(setInflight).catch(() => setInflight([]));
  }, []);

  async function submit() {
    setBusy(true);
    try {
      const res = await dispatchProject(
        source === "manual"
          ? { source, manual: { name: manualName } }
          : { source, url },
      );
      if (!res.created) {
        toast(`Resuming existing session for ${res.slug}.`, { tone: "success" });
      }
      onDispatched(res.session_id);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = source === "manual" ? manualName.trim() !== "" : url.trim() !== "";

  return (
    <div style={{ display: "grid", gap: 16, maxWidth: 720 }}>
      <div style={{ ...cardWrap, padding: 20 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>Source</h3>
        <label style={lbl}>Source site</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {(["cos", "stacked", "manual"] as StagingSource[]).map((s) => (
            <button key={s} type="button" onClick={() => setSource(s)}
              style={{ ...smallBtn, background: source === s ? "var(--accent)" : "var(--surface-2,#f1f1f0)", color: source === s ? "#fff" : "var(--ink-2)" }}>
              {s}
            </button>
          ))}
        </div>
        {source === "manual" ? (
          <>
            <label style={lbl}>Project name</label>
            <input value={manualName} onChange={(e) => setManualName(e.target.value)}
              placeholder="e.g. Some New Launch" style={inp} />
            <p style={hint}>A slug is derived from the name. Use this for a project neither cos nor Stacked covers.</p>
          </>
        ) : (
          <>
            <label style={lbl}>Listing URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder={source === "cos" ? "https://www.cos.sg/condo/…" : "https://www.stackedhomes.com/…"} style={inp} />
            <p style={hint}>The slug is derived from the URL's last path segment. Re-dispatching a live slug resumes its session.</p>
          </>
        )}
        <button type="button" onClick={submit} disabled={!canSubmit || busy} style={{ ...primaryBtn, marginTop: 6, opacity: !canSubmit || busy ? 0.5 : 1 }}>
          {busy ? "Dispatching…" : "Dispatch crawl →"}
        </button>
      </div>

      {inflight.length > 0 && (
        <div style={{ ...cardWrap, padding: 20 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Resume an in-flight session</h3>
          <p style={hint}>Sessions that haven't been committed or abandoned yet.</p>
          <div style={{ display: "grid", gap: 6 }}>
            {inflight.map((s) => (
              <button key={s.id} type="button" onClick={() => onResume(s.id)}
                style={{ ...ghostBtn, textAlign: "left", display: "flex", justifyContent: "space-between" }}>
                <span>{s.slug}</span>
                <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{s.source} · {s.state}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── step 2: extract (poll crawl events) ────────────────────────────

function ExtractStep({
  session, onPoll, onNext,
}: {
  session: StagingSession;
  onPoll: () => Promise<StagingSession | null>;
  onNext: () => void;
}) {
  // Poll every 2.5s while on this step so crawl progress streams in.
  useEffect(() => {
    const t = setInterval(() => { onPoll().catch(() => {}); }, 2500);
    return () => clearInterval(t);
  }, [onPoll]);

  const payload = session.last_step_payload;
  const hasEvents = payload != null && (typeof payload !== "object" || Object.keys(payload as object).length > 0);

  return (
    <div style={{ ...cardWrap, padding: 20, maxWidth: 820 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Extract</h3>
      <p style={hint}>
        Crawl + floorplan extraction run on the data engine. Progress streams below as the
        subprocess reports it. (Extraction needs the Python scripts configured on the engine;
        if nothing appears, the pipeline isn't enabled in this environment.)
      </p>
      <div style={{ background: "var(--surface-2,#0d0d0d0a)", borderRadius: 8, padding: 14, fontFamily: "ui-monospace, monospace", fontSize: 12, minHeight: 80, whiteSpace: "pre-wrap", color: "var(--ink-2)" }}>
        {hasEvents ? JSON.stringify(payload, null, 2) : "No crawl events yet…"}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button type="button" onClick={() => onPoll()} style={ghostBtn}>Refresh</button>
        <button type="button" onClick={onNext} style={primaryBtn}>Continue to rewrite →</button>
      </div>
    </div>
  );
}

// ─── step 3: rewrite ────────────────────────────────────────────────

function RewriteStep({ sessionId, onNext }: { sessionId: string; onNext: () => void }) {
  const toast = useToast();
  const [payload, setPayload] = useState<RewritePayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [unsupported, setUnsupported] = useState(false);

  // Try to load a cached preview on mount (resume); ignore 404 (none yet).
  useEffect(() => {
    getRewritePreview(sessionId).then(setPayload).catch(() => {});
  }, [sessionId]);

  async function run() {
    setBusy(true);
    setUnsupported(false);
    try {
      setPayload(await runRewrite(sessionId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setUnsupported(true);
      } else if (e instanceof ApiError && e.status === 409) {
        toast(e.message, { tone: "error" });
      } else {
        toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
      }
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    setBusy(true);
    try {
      await applyRewrite(sessionId);
      toast("Rewritten text applied to the staged row.", { tone: "success" });
      onNext();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ ...cardWrap, padding: 20, maxWidth: 920 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Rewrite source-flavoured prose</h3>
      <p style={hint}>
        Strips source branding / tone from the crawled description, tagline and key features.
        Review the diff, then apply.
      </p>
      {unsupported && (
        <div style={warnBox}>
          The rewrite pipeline isn't enabled in this environment (needs the Python
          <code> --ndjson</code> step). You can skip this step.
        </div>
      )}
      {!payload ? (
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={run} disabled={busy} style={primaryBtn}>
            {busy ? "Running…" : "Run rewrite"}
          </button>
          <button type="button" onClick={onNext} style={ghostBtn}>Skip →</button>
        </div>
      ) : (
        <>
          {(["description", "tagline", "key_features"] as const).map((f) => (
            <div key={f} style={{ marginBottom: 14 }}>
              <label style={lbl}>{f.replace("_", " ")}</label>
              <div
                style={{ background: "var(--surface-2,#f6f6f5)", borderRadius: 8, padding: 12, fontSize: 13, lineHeight: 1.5 }}
                // diff_html is server-rendered (LCS diff with escaped text,
                // PROP-60). Safe to inject — the server escapes the content
                // and only adds its own <span class="diff-*"> wrappers.
                dangerouslySetInnerHTML={{ __html: payload.diff_html[f] || "<em>(empty)</em>" }}
              />
            </div>
          ))}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={apply} disabled={busy} style={primaryBtn}>
              {busy ? "Applying…" : "Apply & continue →"}
            </button>
            <button type="button" onClick={run} disabled={busy} style={ghostBtn}>Re-run</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── step 4: enrich (informational) ─────────────────────────────────

function EnrichStep({ onNext }: { onNext: () => void }) {
  return (
    <div style={{ ...cardWrap, padding: 20, maxWidth: 720 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Enrich</h3>
      <p style={hint}>
        Geo enrichment (OneMap → postal / lat-lng / MRT / planning area) and the URA
        cross-match run automatically during the crawl. URA↔listing matching stops at
        tier 1+2 — tier-3 fuzzy matching is intentionally skipped (unreliable for shared
        SG condo name suffixes). Review the resulting fields against the source in the
        next step.
      </p>
      <button type="button" onClick={onNext} style={primaryBtn}>Continue to review →</button>
    </div>
  );
}

// ─── step 5: review (link to reconcile inspector) ───────────────────

function ReviewStep({ slug, onNext }: { slug: string; onNext: () => void }) {
  return (
    <div style={{ ...cardWrap, padding: 20, maxWidth: 720 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Review &amp; reconcile</h3>
      <p style={hint}>
        Open the reconciliation inspector to fix any extraction errors — plan-type
        misclassification, multi-storey splits, bbox drift, or wrong cos/stacked fields.
        URA-canonical fields stay read-only. Then return here to scan for source leaks.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <Link to={`/dataengine/reconcile/${encodeURIComponent(slug)}`} style={{ ...primaryBtn, textDecoration: "none" }}>
          Open inspector for {slug} ↗
        </Link>
        <button type="button" onClick={onNext} style={ghostBtn}>Done — scan for leaks →</button>
      </div>
    </div>
  );
}

// ─── step 6: leak-scan (gates commit) ───────────────────────────────

function LeakStep({
  sessionId, slug, onCommit,
}: {
  sessionId: string;
  slug: string;
  onCommit: () => void;
}) {
  const toast = useToast();
  const [scan, setScan] = useState<LeakScanResult | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    runLeakScan(sessionId)
      .then(setScan)
      .catch((e) => toast(e instanceof ApiError ? e.message : String(e), { tone: "error" }))
      .finally(() => setBusy(false));
  }, [sessionId, toast]);

  useEffect(load, [load]);

  async function accept(hitId: string) {
    const reason = window.prompt("Reason for accepting this hit (kept in the audit log):");
    if (reason == null || reason.trim() === "") return;
    try {
      await acceptLeakHit(sessionId, hitId, reason.trim());
      load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    }
  }

  const unresolved = scan?.unresolved ?? 0;
  const gated = !scan || unresolved > 0;

  return (
    <div style={{ ...cardWrap, padding: 20, maxWidth: 900 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Source-leak scan</h3>
      <p style={hint}>
        No upstream source identifier may reach production. Edit the flagged field in the
        inspector, or accept the hit with a reason. Commit is blocked until every hit is
        resolved.
      </p>
      {busy && !scan ? (
        <div style={{ color: "var(--ink-3)" }}>Scanning…</div>
      ) : !scan || scan.hits.length === 0 ? (
        <div style={{ ...okBox }}>No source-leak hits — clear to commit.</div>
      ) : (
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          {scan.hits.map((h) => (
            <div key={h.id} style={{
              border: "1px solid var(--rule,#e3e3e0)", borderRadius: 8, padding: 12,
              background: h.accepted ? "var(--accent-soft,#e6f0ea)" : "rgba(122,45,31,0.05)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 13 }}>
                  <strong>{h.field}</strong> matched <code>{h.match}</code>
                  <div style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 2 }}>…{h.snippet}…</div>
                </div>
                {h.accepted ? (
                  <span style={{ color: "var(--accent)", fontSize: 12, fontWeight: 600 }}>✓ accepted</span>
                ) : (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <Link to={`/dataengine/reconcile/${encodeURIComponent(slug)}`} style={{ ...smallBtn, textDecoration: "none" }}>Edit</Link>
                    <button type="button" onClick={() => accept(h.id)} style={smallBtn}>Accept…</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" onClick={load} style={ghostBtn}>Re-scan</button>
        <button type="button" onClick={onCommit} disabled={gated} style={{ ...primaryBtn, opacity: gated ? 0.5 : 1 }}>
          {gated ? `${unresolved} hit${unresolved === 1 ? "" : "s"} to resolve` : "Proceed to commit →"}
        </button>
      </div>
    </div>
  );
}

// ─── step 7: commit ─────────────────────────────────────────────────

function CommitStep({
  session, onCommitted,
}: {
  session: StagingSession;
  onCommitted: () => Promise<StagingSession | null>;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CommitResult | null>(null);
  const committed = session.state === "committed";

  async function commit() {
    if (!window.confirm(`Commit "${session.slug}" live? Staged rows flip to production in one transaction.`)) return;
    setBusy(true);
    try {
      const res = await commitSession(session.id);
      setResult(res);
      await onCommitted();
      toast(`${res.slug} is live (audit #${res.audit_id}).`, { tone: "success" });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    } finally {
      setBusy(false);
    }
  }

  if (committed || result) {
    return (
      <div style={{ ...cardWrap, padding: 24, maxWidth: 620, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
        <h3 style={{ margin: "0 0 6px", fontSize: 16 }}>{session.slug} is live</h3>
        <p style={hint}>
          The project is now buyer-visible.{result ? ` Audit entry #${result.audit_id}.` : ""}
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
          <Link to="/dataengine/addproject" style={{ ...primaryBtn, textDecoration: "none" }}>Add another</Link>
          <Link to={`/dataengine/reconcile/${encodeURIComponent(session.slug)}`} style={{ ...ghostBtn, textDecoration: "none" }}>View in inspector ↗</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...cardWrap, padding: 20, maxWidth: 620 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Commit</h3>
      <p style={hint}>
        The trust boundary. On commit, the staged rows for <strong>{session.slug}</strong> flip
        live in a single transaction. URA-canonical fields are preserved.
      </p>
      <button type="button" onClick={commit} disabled={busy} style={primaryBtn}>
        {busy ? "Committing…" : "Commit live →"}
      </button>
    </div>
  );
}

// ─── shared styles ──────────────────────────────────────────────────

const lbl: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: "var(--ink-2)", marginBottom: 5 };
const inp: React.CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid var(--rule,#dcdcd8)", fontSize: 14, marginBottom: 6, boxSizing: "border-box" };
const hint: React.CSSProperties = { fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.5, margin: "0 0 14px" };
const warnBox: React.CSSProperties = { background: "rgba(176,124,32,0.1)", color: "#7a5320", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 14 };
const okBox: React.CSSProperties = { background: "var(--accent-soft,#e6f0ea)", color: "var(--accent)", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 14 };
